import { CountWithTitle, Status, TextLink } from '@mm/components';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useChatStartersCRUDActions } from '@mm/state/actions/ChatStartersCRUDActinos';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { mapUIFilterKindToChatStarterKind } from '@mm/state/helpers/chat-starters-helpers';
import {
  ChatStarterKindSnakeType,
  ChatStarterType,
} from '@mm/state/model/ChatStarterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { UIChatStarterType } from '@mm/state/reducers/chatStarters';
import { selectChatStarterRecordsByUiFilterKind } from '@mm/state/selectors/chatStarters';
import { selectCurrentUserIsAdmin } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { GridCell } from '../../../../../../common/components/Grid/Grid';
import TableGrid from '../../../../../../common/components/TableGrid/TableGrid';
import { ThreeDotMenu } from '../../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import { formatDateTime } from '../../../../../../common/helpers/formatDateTime';
import { trackWebAppGAEvent } from '../../../../../../common/helpers/plugins/website-google-analytics';
import { useShowInactiveBannerFlag } from '../../../../../common/hooks/use-show-inactive-bot-banner-flag';
import { useCatchAllConfirmModal } from '../../../../startBotPromoters/commentGuards/use-catch-all-confirm-modal';
import { pathBuilder } from '../pathBuilder';
import { starterTypeByKind } from '../types';
import { useDeleteChatStarter } from '../use-delete-chat-starter';
import './StartersTable.pcss';

const DEFAULT_PER_PAGE_QTY = 8;
const DEFAULT_PAGE = 1;

type UIChatStarterTypeWithPath = UIChatStarterType & {
  path: string;
};

const generatePostId = (postId, subKind) => {
  if ((!postId || postId === 'ALL') && subKind === 'all') {
    return 'All Posts / All Comments';
  }
  if ((!postId || postId === 'ALL') && subKind === 'keywords') {
    return 'All Posts / Keywords';
  }
  if (postId === 'id') {
    return 'Not Selected Post';
  }
  if (postId && postId !== 'id') {
    return postId;
  }
  return postId;
};

const renderNameCell = ({ name, id }: UIChatStarterType) => {
  return (
    <GridCell
      key={`${id}-name`}
      fr={2}
      className="starters-table__starter-name"
    >
      {name}
    </GridCell>
  );
};

const renderKindCell = ({ filterKind, id }: UIChatStarterType) => {
  const { name, icon: Icon } = starterTypeByKind[filterKind] || {};
  return (
    <GridCell key={`${id}-kind`} fr={2}>
      <TextLink
        className="starters-table__starter-kind"
        icon={Icon && <Icon />}
      >
        <span className="starters-table__starter-kind-text">{name}</span>
      </TextLink>
    </GridCell>
  );
};

const renderPostIdCell = ({
  postId,
  id,
  filterKind,
  subKind,
}: UIChatStarterType) => {
  const isCommentGuard =
    filterKind === 'comment_guard' || filterKind === 'instagram_comment_guard';
  if (!isCommentGuard) return <></>;
  return (
    <GridCell key={`${id}-post-id`} fr={1}>
      {isCommentGuard && (
        <TextLink className="starters-table__starter-kind">
          <span className="starters-table__starter-kind-text">
            Post ID: {generatePostId(postId, subKind)}
          </span>
        </TextLink>
      )}
    </GridCell>
  );
};

const renderStatusCell = ({ active, id }: UIChatStarterType) => {
  return (
    <GridCell key={`${id}-status`}>
      <Status status={active ? 'Active' : 'Inactive'} />
    </GridCell>
  );
};

const renderChatCountCell = ({
  active,
  startsCount,
  id,
}: UIChatStarterType) => {
  return (
    <GridCell key={`${id}-count`}>
      <CountWithTitle isActive={active} count={startsCount} text="Chats" />
    </GridCell>
  );
};

const renderStartedAtCell = ({ starterDateTime, id }: UIChatStarterType) => {
  const date = formatDateTime(starterDateTime);
  return <GridCell key={`${id}-started`}>Started at: {date}</GridCell>;
};

const ThreeDotMenuCell = (starter: UIChatStarterTypeWithPath) => {
  const { filterKind, id, path, active } = starter;
  const { deleteChatStarter } = useDeleteChatStarter();
  const promoterCRUDActions = usePromoterCRUDActions();
  const viewActions = useViewActions();
  const chatStartersCRUDActions = useChatStartersCRUDActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const botCRUDActions = useChatbotCRUDActions();
  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const userIsAdmin = useSelector(selectCurrentUserIsAdmin);

  const modalActions = useModalActions();
  const { onClickActivate } = useShowInactiveBannerFlag();
  const instagramCGStarters = useSelector((state: StoreState) =>
    selectChatStarterRecordsByUiFilterKind(state, 'instagram_comment_guard'),
  );
  const instagramLiveStarters = useSelector((state: StoreState) =>
    selectChatStarterRecordsByUiFilterKind(
      state,
      'instagram_live_comment_guard',
    ),
  );
  const records = useSelector((state: StoreState) =>
    selectChatStarterRecordsByUiFilterKind(state, filterKind),
  );

  const openCatchAllConfirmModal = useCatchAllConfirmModal(
    'Important Note',
    `Activating this catch-all ${
      filterKind === 'instagram_comment_guard'
        ? 'Comment guard'
        : 'Instagram Live'
    } will disable all other catch-all ${
      filterKind === 'instagram_comment_guard'
        ? 'Comment guards'
        : 'Instagram Lives'
    }. Are you sure you would like to proceed?`,
  );
  const remoteCatchAllCG = instagramCGStarters?.chatStarters.find(
    starter => starter.subKind === 'all' && starter.active,
  );
  const remoteCatchAllIgLiveCG = instagramLiveStarters?.chatStarters.find(
    starter => starter.subKind === 'all' && starter.active,
  );
  const { history } = useRouter();

  const getActivateActionCG = (
    remoteCatchAll?: ChatStarterType,
    chatStarters?: UIChatStarterType[],
    kind?: ChatStarterKindSnakeType,
  ) => {
    const showCatchAllModal =
      remoteCatchAll &&
      remoteCatchAll.id !== starter.id &&
      starter?.subKind === 'all';

    const instagramCatchAllPromoterToDeactivate = chatStarters?.find(
      promoter => promoter.id === remoteCatchAll?.id && promoter.active,
    );
    const baseActivateAction = async () =>
      promoterCRUDActions.update(chatbotId, {
        id: starter.id,
        active: true,
      });
    const deactivateAnotherAction = () => {
      openCatchAllConfirmModal(async () => {
        await baseActivateAction();
        if (instagramCatchAllPromoterToDeactivate) {
          await promoterCRUDActions.update(chatbotId, {
            id: instagramCatchAllPromoterToDeactivate.id,
            active: false,
          });
        }
        if (kind) {
          chatStartersCRUDActions.retrieveAll({
            botId: chatbotId,
            kind,
            pagination: {
              page: records?.pagination?.page || DEFAULT_PAGE,
              perPage: records?.pagination?.perPage || DEFAULT_PER_PAGE_QTY,
            },
          });
        }
      });
    };
    return showCatchAllModal ? deactivateAnotherAction : baseActivateAction;
  };

  const onSelectItem = async (
    v: 'edit' | 'delete' | 'activate' | 'deactivate' | 'editInSlideout',
  ) => {
    const kind = mapUIFilterKindToChatStarterKind(filterKind);
    switch (v) {
      case 'edit':
        history.push(path);
        break;

      case 'delete':
        deleteChatStarter(starter);
        break;

      case 'activate': {
        let activateAction;
        if (kind === 'welcome_page') {
          activateAction = async () =>
            botCRUDActions.update({
              id: chatbotId,
              fbWelcomerDisabled: false,
            });
        } else if (kind === 'instagram_comment_guard') {
          activateAction = getActivateActionCG(
            remoteCatchAllCG,
            instagramCGStarters?.chatStarters,
            kind,
          );
        } else if (kind === 'instagram_live_comment_guard') {
          activateAction = getActivateActionCG(
            remoteCatchAllIgLiveCG,
            instagramLiveStarters?.chatStarters,
            kind,
          );
        } else {
          activateAction = async () =>
            promoterCRUDActions.update(chatbotId, {
              id: starter.id,
              active: true,
            });
        }

        const activateAndUpdateStatus = async () => {
          await activateAction();
          if (kind) {
            chatStartersCRUDActions.retrieveAll({
              botId: chatbotId,
              kind,
              pagination: {
                page: records?.pagination?.page || DEFAULT_PAGE,
                perPage: records?.pagination?.perPage || DEFAULT_PER_PAGE_QTY,
              },
            });
          }
        };

        if (!chatbotIsActiveOnConnectedPage) {
          modalActions.openActivateInactiveBotModal({
            onBotActivateConfirm: () =>
              setTimeout(() => onClickActivate(activateAndUpdateStatus), 500),
            useCustomOnBotActivateConfirm: true,
          });
        } else {
          activateAndUpdateStatus();
        }
        break;
      }

      case 'deactivate':
        if (kind === 'welcome_page') {
          await botCRUDActions.update({
            id: chatbotId,
            fbWelcomerDisabled: true,
          });
        } else {
          await promoterCRUDActions.update(chatbotId, {
            id: starter.id,
            active: false,
          });
        }
        if (kind) {
          chatStartersCRUDActions.retrieveAll({
            botId: chatbotId,
            kind,
            pagination: {
              page: records?.pagination?.page || DEFAULT_PAGE,
              perPage: records?.pagination?.perPage || DEFAULT_PER_PAGE_QTY,
            },
          });
        }
        break;
      case 'editInSlideout':
        if (kind === 'sms_keywords') {
          viewActions.showSlideoutMenu('smsKeywordEditor', {
            triggerId: id,
          });
        } else if (kind === 'instagram_welcomer') {
          viewActions.showSlideoutMenu('igWelcomerEditor');
        } else if (kind === 'welcome_page') {
          viewActions.showSlideoutMenu('fbWelcomerEditor');
        } else if (kind === 'fb_comment_guard') {
          viewActions.showSlideoutMenu('fbCommentGuardEditor', {
            promoterId: id,
          });
        } else if (kind === 'link') {
          viewActions.showSlideoutMenu('shareLinksEditor', {
            promoterId: id,
            platform: 'facebook',
          });
        } else if (kind === 'instagram_comment_guard') {
          viewActions.showSlideoutMenu('igCommentGuardEditor', {
            promoterId: id,
          });
        } else if (kind === 'instagram_live_comment_guard') {
          viewActions.showSlideoutMenu('igLiveEditor', {
            promoterId: id,
          });
        } else if (kind === 'instagram_story_mention') {
          viewActions.showSlideoutMenu('igStoryMentionEditor', {
            promoterId: id,
          });
        } else if (kind === 'instagram_story_reaction') {
          viewActions.showSlideoutMenu('igStoryReactionEditor', {
            promoterId: id,
          });
        } else if (kind === 'customer_chat') {
          viewActions.showSlideoutMenu('customerChatSlideoutEditor', {
            promoterId: id,
          });
        }
        break;
      default:
    }
  };

  const noDeleteAction =
    filterKind === 'facebook_page_welcomer' ||
    filterKind === 'instagram_welcomer';

  const noActivateDeactivateActions =
    filterKind === 'landing_pages' || filterKind === 'sms_keywords';

  const editInSlideout =
    userIsAdmin &&
    [
      'sms_keywords',
      'instagram_welcomer',
      'facebook_page_welcomer',
      'comment_guard',
      'share_links',
      'instagram_comment_guard',
      'instagram_live_comment_guard',
      'instagram_story_mention',
      'instagram_story_reaction',
      'customer_chat_widget',
    ].includes(filterKind);

  const elements = [
    ...(noActivateDeactivateActions
      ? []
      : [
          active
            ? { name: 'Deactivate', value: 'deactivate' }
            : {
                name: 'Activate',
                value: 'activate',
              },
        ]),
    { name: 'Edit', value: 'edit' },
    ...(noDeleteAction ? [] : [{ name: 'Delete', value: 'delete' }]),
    ...(editInSlideout
      ? [{ name: 'Edit in Slideout', value: 'editInSlideout' }]
      : []),
  ];

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <GridCell
      key={`${id}-menu-inner`}
      className="starters-table__starter-menu-cell"
      onClick={onClick}
      overflowable
    >
      <ThreeDotMenu
        className="starters-table__starter-menu"
        onClick={onClick}
        elements={elements}
        selectItem={onSelectItem}
      />
    </GridCell>
  );
};

const getStartersTableDataMapper = () => [
  {
    label: 'name',
    keyName: 'name',
    renderCell: renderNameCell,
  },
  {
    label: 'kind',
    keyName: 'filterKind',
    renderCell: renderKindCell,
  },
  {
    label: 'postId',
    keyName: 'postId',
    renderCell: renderPostIdCell,
  },
  {
    label: 'createdAt',
    keyName: 'createdAd',
    renderCell: renderStartedAtCell,
  },
  {
    label: 'status',
    keyName: 'status',
    renderCell: renderStatusCell,
  },
  {
    label: 'chatsCount',
    keyName: 'startsCount',
    renderCell: renderChatCountCell,
  },
  {
    label: '',
    renderCell: (starter: UIChatStarterTypeWithPath) => (
      <ThreeDotMenuCell key={`${starter.id}-menu`} {...starter} />
    ),
  },
];

type Props = {
  className?: string;
  starters?: UIChatStarterType[];
};

export const StartersTable = ({ className, starters = [] }: Props) => {
  const { history } = useRouter();
  const botId = useSelector(activeChatbotIdSelector);

  const items: UIChatStarterTypeWithPath[] = starters.map(o => ({
    ...o,
    rowClassName: classNames('starters-table__data-row', {
      'starters-table__data-row--inactive': !o.active,
    }),
    path: pathBuilder.buildResourceUrl(botId, o),
  }));
  const data = getStartersTableDataMapper();

  const onRowClick = (id: number, starter: UIChatStarterTypeWithPath) => {
    trackWebAppGAEvent('click', {
      category: 'starters_table',
      label: `table_item_${starter.filterKind}`,
    });
    const { path } = starter;
    history.push(path);
  };

  return (
    <TableGrid
      className={className}
      data={data}
      items={items}
      onRowClick={onRowClick}
      hideLabels
      hideEmptyHeader
    />
  );
};
